import { Download, Plus } from 'lucide-react';
import { useAuth } from '@platform/auth-client';
import { Button, PageHeader, SearchBox } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { ORDERS_WRITE_ROLE } from '../customers/customers.ts';
import { namesOf, useGeoAreas } from '../geography/useGeoAreas.ts';
import { ResourceTable } from '../ResourceTable.tsx';
import { usePageContext } from '../usePageContext.ts';
import { useShortcuts } from '../useShortcuts.ts';
import { useCarriers } from '../settings/carriers.ts';
import { useBulkOrderActions, eligibleFor, type BulkKind } from './bulk.ts';
import { BulkOutcomeSheet } from './BulkOutcomeSheet.tsx';
import { CancelOrderDialog } from './CancelOrderDialog.tsx';
import { DispatchOrderDialog } from './DispatchOrderDialog.tsx';
import { ExportOrdersDialog } from './ExportOrdersDialog.tsx';
import { OrderBulkBar } from './OrderBulkBar.tsx';
import { OrderFilterBar } from './OrderFilterBar.tsx';
import { isFiltered, narrowingQueryOf, type OrderFilters } from './order-filters.ts';
import { OrderPanels } from './OrderPanels.tsx';
import { isAtCarrierBeforePickup, type OrderView } from './orders.ts';
import { OrderViewChips } from './OrderViewChips.tsx';
import tools from '../ListTools.module.css';
import styles from './Orders.module.css';
import { useOrderActionsColumn } from './useOrderActionsColumn.tsx';
import { useOrderColumns } from './useOrderColumns.tsx';
import { useOrderMoves } from './useOrderMoves.ts';
import { chosenLabelsOf, useOrderFilterOptions } from './useOrderFilterOptions.ts';
import { ORDER_PAGE_SIZE, useOrderList, useOrderViewCounts } from './useOrderList.ts';
import { useOrderLookup } from './useOrderLookup.ts';

export type OrdersPageProps = {
  api: TypedClient;
  shipping: ShippingClient;
  view: OrderView;
  filters: OrderFilters;
  openOrderId: string | null;
  onViewChange: (view: OrderView) => void;
  onFiltersChange: (filters: OrderFilters) => void;
  onOpenOrder: (orderId: string | null) => void;
  onCreate: () => void;
  onEdit: (orderId: string) => void;
  onOpenSettings: () => void;
};

type EmptyContext = { readonly searching: boolean; readonly view: OrderView; readonly filtered: boolean };

const emptyKeyOf = ({ searching, view, filtered }: EmptyContext): string => {
  if (searching) return 'noSearchMatch';
  return view === 'all' && !filtered ? 'noOrders' : 'noMatch';
};

type Selection = { readonly view: OrderView; readonly ids: ReadonlySet<string> };

type BulkDialog = 'dispatch' | 'cancel' | null;

export const OrdersPage = (props: OrdersPageProps) => {
  const { api, shipping, view, filters, openOrderId, onViewChange, onFiltersChange, onOpenOrder, onCreate } =
    props;
  const { onOpenSettings } = props;
  const { t, describeError } = usePageContext();
  const canWrite = useAuth().hasRole(ORDERS_WRITE_ROLE);
  const [openedAt] = useState(() => new Date());
  const narrowing = narrowingQueryOf(filters, openedAt);
  const list = useOrderList(api, view, narrowing);
  const counts = useOrderViewCounts(api, narrowing).data;
  const geoNames = namesOf(useGeoAreas(api).data);
  const carriers = useCarriers(shipping);
  const bulk = useBulkOrderActions(api, shipping);
  const [selection, setSelection] = useState<Selection>({ view, ids: new Set() });
  const [bulkDialog, setBulkDialog] = useState<BulkDialog>(null);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const filterOptions = useOrderFilterOptions(api, shipping);
  const lookup = useOrderLookup(api, search);
  const moves = useOrderMoves(api, props.onEdit);
  const actionsColumn = useOrderActionsColumn(shipping, moves, (order) => {
    onOpenOrder(order.id);
  });
  const columns = [...useOrderColumns(shipping, geoNames), actionsColumn];
  const rows = lookup.active ? lookup.orders : list.items;
  const selectedIds = selection.view === view ? selection.ids : new Set<string>();
  const selected = rows.filter((order) => selectedIds.has(order.id));
  const select = (ids: ReadonlySet<string>) => {
    setSelection({ view, ids });
  };

  useShortcuts(canWrite ? { n: onCreate } : {});

  const emptyKey = emptyKeyOf({ searching: lookup.active, view, filtered: isFiltered(filters) });
  const emptyTitle = t(`orders.${emptyKey}`);
  const emptyDescription = t(`orders.${emptyKey}Help`);

  const act = (kind: BulkKind) => {
    bulk.reset();
    if (kind === 'dispatch' || kind === 'cancel') setBulkDialog(kind);
    else bulk.mutate({ kind, orders: selected }, { onSuccess: () => select(new Set()) });
  };
  const closeBulkDialog = () => {
    setBulkDialog(null);
  };
  const finishBulkDialog = () => {
    setBulkDialog(null);
    select(new Set());
  };
  const bulkError = bulk.isError ? (describeError(bulk.error) ?? null) : null;
  const toCancel = eligibleFor('cancel', selected);
  const retryDispatch = (orderIds: readonly string[]) => {
    bulk.reset();
    select(new Set(orderIds));
    setBulkDialog('dispatch');
  };

  return (
    <>
      <PageHeader
        title={t('nav.orders')}
        context={t('orders.context')}
        action={
          canWrite ? (
            <div className={styles['headerActions']}>
              <Button
                variant="outline"
                icon={<Download size={20} aria-hidden="true" />}
                onClick={() => {
                  setExporting(true);
                }}
              >
                {t('orders.export')}
              </Button>
              <Button icon={<Plus size={20} aria-hidden="true" />} onClick={onCreate}>
                {t('orders.newOrder')}
              </Button>
            </div>
          ) : undefined
        }
      />
      <OrderViewChips view={view} counts={counts} onChange={onViewChange} />
      <ResourceTable
        query={list.query}
        pagination={
          lookup.active
            ? undefined
            : { page: list.page, pageSize: ORDER_PAGE_SIZE, total: list.total, onPageChange: list.turnTo }
        }
        columns={columns}
        rows={rows}
        toolbar={
          <div className={tools['listTools']}>
            <SearchBox
              label={t('orders.searchLabel')}
              placeholder={t('orders.searchPlaceholder')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              onSearch={setSearch}
            />
            <OrderFilterBar api={api} shipping={shipping} filters={filters} onChange={onFiltersChange} />
            <span className={styles['muted']} aria-live="polite">
              {lookup.active ? t('orders.searchScope') : t('orders.sortedNewest')}
            </span>
          </div>
        }
        rowKey={(order) => order.id}
        activeKey={openOrderId}
        selection={{
          selected: selectedIds,
          onChange: select,
          rowLabel: (order) => t('orders.selectRow', { number: order.number }),
          allLabel: t('orders.selectAll'),
        }}
        onRowClick={(order) => {
          onOpenOrder(order.id);
        }}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
      />
      {selected.length === 0 ? null : (
        <OrderBulkBar
          selected={selected}
          busy={bulk.isPending}
          onAction={act}
          onClear={() => select(new Set())}
        />
      )}
      <OrderPanels {...props} geoNames={geoNames} moves={moves} />
      {exporting ? (
        <ExportOrdersDialog
          api={api}
          scope={{
            view,
            narrowing,
            total: list.total,
            summary: [t(`orders.view_${view}`), ...chosenLabelsOf(filters, filterOptions)].join(' · '),
          }}
          onClose={() => {
            setExporting(false);
          }}
        />
      ) : null}
      {bulkDialog === 'dispatch' ? (
        <DispatchOrderDialog
          title={t('orderBulk.dispatchTitle', { count: eligibleFor('dispatch', selected).length })}
          message={t('orderBulk.dispatchMessage')}
          orders={eligibleFor('dispatch', selected)}
          carriers={carriers.data?.items ?? []}
          loadingCarriers={carriers.isPending}
          busy={bulk.isPending}
          errorMessage={bulkError}
          onOpenSettings={onOpenSettings}
          onClose={closeBulkDialog}
          onDispatch={(carrierCode, fees, idempotencyKey) => {
            bulk.mutate(
              { kind: 'dispatch', orders: selected, carrierCode, fees, idempotencyKey },
              { onSuccess: finishBulkDialog },
            );
          }}
        />
      ) : null}
      {bulkDialog === 'cancel' ? (
        <CancelOrderDialog
          title={t('orderBulk.cancelTitle', { count: toCancel.length })}
          notes={[
            t('orderBulk.cancelMessage'),
            toCancel.some(isAtCarrierBeforePickup)
              ? t('orderBulk.cancelAtCarrier', { count: toCancel.filter(isAtCarrierBeforePickup).length })
              : t('orderBulk.cancelHereOnly'),
          ]}
          confirmLabel={t('orderBulk.cancelConfirm', { count: toCancel.length })}
          keepLabel={t('orderBulk.keepOrders')}
          busy={bulk.isPending}
          errorMessage={bulkError}
          onKeep={closeBulkDialog}
          onCancel={(reason) => {
            bulk.mutate({ kind: 'cancel', orders: selected, reason }, { onSuccess: finishBulkDialog });
          }}
        />
      ) : null}
      {bulk.data === undefined ? null : (
        <BulkOutcomeSheet
          outcome={bulk.data}
          orders={rows}
          api={api}
          shipping={shipping}
          onOpenSettings={onOpenSettings}
          onRetryDispatch={retryDispatch}
          onClose={() => {
            bulk.reset();
          }}
        />
      )}
    </>
  );
};
